/**
 * EnablePushBanner — banner inline para invitar al usuario a activar push.
 *
 * Aparece SOLO si:
 *   - El navegador soporta Web Push (no iOS Safari fuera de PWA).
 *   - El usuario aún no concedió el permiso (permission='default').
 *   - El usuario no clickeó "ya, después" en esta sesión.
 *
 * Render con vibe Aircom (esmeralda). Botón cierra-permanente por sesión.
 */
import { useEffect, useState } from 'react';
import { Bell, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import {
  currentPermission, getActiveSubscription, isSupported, subscribe,
} from '@/utils/push';

const DISMISS_KEY = 'aircom.push.banner.dismissed_v1';

const EnablePushBanner = ({ audience = 'gestor' }) => {
  const { getAuthHeaders } = useAuth();
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isSupported()) return;
    if (sessionStorage.getItem(DISMISS_KEY)) return;
    const perm = currentPermission();
    if (perm === 'granted') {
      // Verificar si efectivamente hay subscription registrada.
      getActiveSubscription().then((s) => setShow(!s)).catch(() => null);
    } else if (perm === 'default') {
      setShow(true);
    }
  }, []);

  if (!show) return null;

  const onEnable = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await subscribe(getAuthHeaders);
      toast.success('¡Listo! Recibirás notificaciones reales en este dispositivo.');
      setShow(false);
    } catch (e) {
      if (e?.code === 'denied' || e?.message === 'permission-denied') {
        toast.error('Permiso denegado. Activá las notificaciones desde tu navegador.');
      } else {
        toast.error('No se pudo activar push: ' + (e?.message || 'error'));
      }
    } finally {
      setBusy(false);
    }
  };

  const onDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setShow(false);
  };

  const message = audience === 'provider'
    ? 'No te pierdas remesas nuevas — recibí un push apenas el CEO te asigne una.'
    : 'Activá las notificaciones para enterarte cuando se entregue tu remesa y se acrediten tus ganancias.';

  return (
    <div
      className="mx-3 sm:mx-5 mt-3 rounded-xl border border-emerald-200 bg-emerald-50/80 px-4 py-3 flex items-center gap-3"
      data-testid="enable-push-banner"
    >
      <div className="shrink-0 w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center">
        <Bell className="w-4 h-4 text-emerald-700" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-emerald-900">Activá tus notificaciones</p>
        <p className="text-xs text-emerald-800/80 leading-snug">{message}</p>
      </div>
      <button
        onClick={onEnable}
        disabled={busy}
        className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold disabled:opacity-60"
        data-testid="enable-push-btn"
      >
        {busy ? 'Activando…' : 'Activar'}
      </button>
      <button
        onClick={onDismiss}
        className="p-1.5 rounded-lg hover:bg-emerald-100 text-emerald-700"
        aria-label="Cerrar"
        data-testid="dismiss-push-btn"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default EnablePushBanner;
